/**
 * vv-wishlist — native wishlist engine (replaces the Vitals wishlist).
 *
 * - localStorage is the source of truth for display ('vv_wl'); every
 *   add/remove is mirrored to the TenQ backend (fire-and-forget beacon)
 *   for analytics + email capture.
 * - Anonymous visitor id in 'vv_wid' (UUID). Logged-in customers get their
 *   email linked to the visitor (transactional only). Customer-id based
 *   cross-device merge was removed — unverifiable without an App Proxy.
 * - Hearts: any element with [data-vv-wl-toggle] + data-pid/handle/title.
 *   Delegated click handler; state class 'is-saved'.
 * - Header: finds Gecko's wishlist icon (svg use #t4s-icon-wis), repoints
 *   it to /pages/wishlist and maintains a count bubble.
 */
(function () {
  'use strict';
  var API = 'https://api.tenq.ro/api/wishlist';
  var SHOP = 'vivendo';
  var LS_KEY = 'vv_wl';
  var WID_KEY = 'vv_wid';

  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  function wid() {
    var v = null;
    try { v = localStorage.getItem(WID_KEY); } catch (e) { /* noop */ }
    if (!v) {
      v = uuid();
      try { localStorage.setItem(WID_KEY, v); } catch (e) { /* noop */ }
    }
    return v;
  }

  function read() {
    try {
      var raw = JSON.parse(localStorage.getItem(LS_KEY) || '[]');
      return Array.isArray(raw) ? raw : [];
    } catch (e) { return []; }
  }
  function write(list) {
    try { localStorage.setItem(LS_KEY, JSON.stringify(list.slice(0, 200))); } catch (e) { /* noop */ }
    document.dispatchEvent(new CustomEvent('vv:wishlist-change', { detail: { count: list.length } }));
  }

  function customerEmail() {
    return (document.body && document.body.dataset.vvCustomerEmail) || null;
  }

  function beacon(path, payload) {
    try {
      fetch(API + path, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }).catch(function () { /* noop */ });
    } catch (e) { /* noop */ }
  }

  var api = {
    list: read,
    count: function () { return read().length; },
    has: function (pid) {
      pid = parseInt(pid, 10);
      return read().some(function (i) { return i.p === pid; });
    },
    add: function (item) {
      var pid = parseInt(item.p, 10);
      if (!pid) return;
      var list = read();
      if (!list.some(function (i) { return i.p === pid; })) {
        list.unshift({ p: pid, v: parseInt(item.v, 10) || null, h: item.h || null, t: item.t || null, pr: item.pr || null });
        write(list);
      }
      beacon('/event', {
        shop: SHOP, visitorId: wid(), action: 'add',
        productId: pid, variantId: item.v || null, handle: item.h || null,
        title: item.t || null, price: item.pr || null
      });
    },
    remove: function (pid) {
      pid = parseInt(pid, 10);
      write(read().filter(function (i) { return i.p !== pid; }));
      beacon('/event', { shop: SHOP, visitorId: wid(), action: 'remove', productId: pid });
    },
    toggle: function (item) {
      if (api.has(item.p)) { api.remove(item.p); return false; }
      api.add(item); return true;
    },
    identify: function (email, consent) {
      return fetch(API + '/identify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop: SHOP, visitorId: wid(), email: email, consent: !!consent })
      }).then(function (r) { return r.json(); });
    },
    visitorId: wid
  };
  window.vvWishlist = api;

  /* ── Heart state sync ─────────────────────────────────────────────── */
  function syncHearts(root) {
    var list = read();
    var ids = {};
    list.forEach(function (i) { ids[i.p] = true; });
    (root || document).querySelectorAll('[data-vv-wl-toggle]').forEach(function (btn) {
      var saved = !!ids[parseInt(btn.dataset.pid, 10)];
      btn.classList.toggle('is-saved', saved);
      btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
    });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-vv-wl-toggle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var saved = api.toggle({
      p: btn.dataset.pid,
      v: btn.dataset.vid,
      h: btn.dataset.handle,
      t: btn.dataset.title,
      pr: btn.dataset.price
    });
    btn.classList.toggle('is-saved', saved);
    btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
    if (saved) {
      btn.classList.add('vv-wl-pop');
      setTimeout(function () { btn.classList.remove('vv-wl-pop'); }, 400);
    }
  });

  /* ── Header heart ──────────────────────────────────────────────────
     Vitals' injected header heart ([class*="Vtl-Wishlist"]) is hidden
     permanently via CSS in vv-header-skin — CSS survives Vitals
     re-injections. We inject exactly ONE heart of our own before the
     header cart icon. */
  var HEART_SVG = '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>';

  function setupHeader() {
    // dedupe: keep only the first of ours (earlier versions could double up)
    var ours = document.querySelectorAll('[data-vv-wl-wrap]');
    for (var i = 1; i < ours.length; i++) ours[i].remove();
    // remove pre-wrapper strays from earlier versions
    document.querySelectorAll('[data-vv-wl-header]').forEach(function (el) {
      if (!el.closest('[data-vv-wl-wrap]')) el.remove();
    });
    if (!document.querySelector('[data-vv-wl-wrap]')) {
      var cart = null;
      document.querySelectorAll('a[href="/cart"], a[href^="/cart?"]').forEach(function (a) {
        if (!cart && a.querySelector('svg') && a.getBoundingClientRect().top < 120) cart = a;
      });
      if (cart) {
        // mirror Gecko's per-icon wrapper so spacing/alignment match the row
        var cartWrap = cart.closest('.t4s-site-nav__icon') || cart.parentElement;
        var wrap = document.createElement(cartWrap.tagName.toLowerCase());
        wrap.className = (cartWrap.className + '')
          .replace(/t4s-site-nav__cart/g, '').replace(/\s+/g, ' ').trim();
        wrap.dataset.vvWlWrap = '1';
        var link = document.createElement('a');
        link.setAttribute('href', '/pages/wishlist');
        link.setAttribute('aria-label', 'Favorite');
        link.dataset.vvWlHeader = '1';
        link.className = 'vv-wl-header-link';
        link.innerHTML = HEART_SVG; // static markup, no data interpolation
        var bubble = document.createElement('span');
        bubble.className = 'vv-wl-header-count';
        link.appendChild(bubble);
        wrap.appendChild(link);
        cartWrap.parentNode.insertBefore(wrap, cartWrap);
      }
    }
    updateHeaderCount();
  }

  function updateHeaderCount() {
    var n = api.count();
    document.querySelectorAll('.vv-wl-header-count').forEach(function (b) {
      b.textContent = n;
      b.classList.toggle('is-visible', n > 0);
    });
  }
  document.addEventListener('vv:wishlist-change', updateHeaderCount);

  /* ── Logged-in email link (transactional only, no marketing consent).
        Customer-id cross-device merge was removed — a client-claimed
        customerId is unverifiable without a Shopify App Proxy. ─────────── */
  function mergeServerList() {
    var email = customerEmail();
    if (email) {
      try {
        var k = 'vv_wl_cid_linked';
        if (sessionStorage.getItem(k) !== email) {
          beacon('/identify', { shop: SHOP, visitorId: wid(), email: email, consent: false });
          sessionStorage.setItem(k, email);
        }
      } catch (e) { /* noop */ }
    }
  }

  function init() {
    syncHearts();
    setupHeader();
    // Gecko renders parts of the header via lazily-loaded main.js — retry
    var tries = 0;
    var t = setInterval(function () {
      tries++;
      setupHeader();
      if (tries > 10) clearInterval(t);
    }, 1000);
    mergeServerList();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // re-sync hearts on dynamically injected content (related slider, FBT)
  document.addEventListener('vv:cards-injected', function (e) { syncHearts(e.target); });
  window.vvWishlistSyncHearts = syncHearts;
})();
